import type { ComponentType, PropsWithChildren } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { I18nProvider, i18nLocales } from '@muse/i18n'
import { renderApp, createBrowserRouter, type TRoutingMap } from '@muse-web/router'
import { configureNextjsApp, getLoaderData, type TBackendApp } from '@core/backend'
import { configureHttpClient } from '@core/http-client'
import { configureOwnedRoutes, isHardNavigationRoute } from '@core/routes'
import { configureAuth } from '@features/auth'
import { configureCheckout } from '@features/checkout'
import { restoreSession } from '@features/session'
import { syncLoggerUser } from '@core/logger'
import { setupTheme } from '@core/theme'
import { queryClient } from '@core/query'
import {
    experiments,
    experimentCookieJar,
    experimentSlugsCookieStorage,
    fetchActiveExperiment,
} from '@core/experiments'
import { createStore, StoreProvider } from '@core/store'
import { type ScreenId } from '@core/router'
import {
    markLoadStage,
    reportExperimentExposure,
    seedCfContext,
    setAnalyticsRelease,
    setRenderMode,
    trackWebVitals,
} from '@core/analytics'
import { isChromelessScreen, setInitialScreen } from '../navigation/useIsChromeless'
import { trackInitialScreenView } from './trackInitialScreenView'
import { AppLoggerProvider } from './AppLoggerProvider'
import { LoadStageMarker } from './load-stage-marker'
import { getNavigationMode } from './navigationMode'
import { routerLogger } from './pageErrorReport'
import { seedStoreChunks } from './seedStoreChunks'
import { stampMigrationMarker } from './stampMigrationMarker'

const APP_LOCALE = i18nLocales.default

type TRenderScreenProps = {
    routes: TRoutingMap<void>
    Layout: ComponentType<PropsWithChildren>
    hydratedScreens?: ScreenId[]
}

export const renderScreen = async ({
    routes,
    Layout,
    hydratedScreens = [],
}: TRenderScreenProps) => {
    markLoadStage('app_start')
    setupTheme()
    stampMigrationMarker()

    const store = createStore()

    const Router = createBrowserRouter<TBackendApp>({
        routes,
        Layout,
        context: undefined,
        config: {
            hydratedScreens,
            logger: routerLogger,
            getLoaderData,
            mode: getNavigationMode(),
            isHardNavigationRoute,
            ssrUrl: window.location.href,
        },
    })

    const { app, initialScreenId } = Router.getAppState()

    setInitialScreen(initialScreenId)

    seedStoreChunks(app)
    syncLoggerUser(app.user)
    setAnalyticsRelease(app.release)
    seedCfContext(app.cloudflare.context)

    configureHttpClient({ baseUrl: app.domains.api })
    restoreSession(app.user.id)

    configureNextjsApp({ origin: app.domains.nextjs })

    configureOwnedRoutes(app.routes.spa)

    configureAuth({
        clientId: app.oauth.clientId,
        clientSecret: app.oauth.clientSecret,
        googleClientId: app.oauth.googleClientId,
        redirectUri: app.oauth.redirectUri,
        apiBaseUrl: app.domains.api,
        providerOverride: app.oauth.providerOverride,
    })

    configureCheckout({ publishableKey: app.stripe.publishableKey })

    // init before render so lazily-loaded screens read the right variations
    experiments.init({
        activeExperiments: app.ab.variations,
        closedExperiments: app.ab.closed_experiments,
        ownSlugs: app.ab.slugs,
        storage: experimentSlugsCookieStorage,
        cookies: experimentCookieJar,
        fetchAssignment: fetchActiveExperiment,
        reportEvent: reportExperimentExposure,
    })

    // Early page_view — fires at boot (backend-rendered page) before React mounts.
    // Soft-nav page_view + session tracking are handled by SessionTracking in the shell.
    trackInitialScreenView(initialScreenId)

    const isScreenHydrated = hydratedScreens.includes(initialScreenId)
    const hasServerChrome = !isChromelessScreen(initialScreenId)

    setRenderMode(isScreenHydrated ? 'hydrate' : 'render')

    if (isScreenHydrated) {
        await Router.preloadScreen()
    }

    await I18nProvider.preload(APP_LOCALE)

    renderApp({
        children: (
            <LoadStageMarker>
                <AppLoggerProvider>
                    <StoreProvider store={store}>
                        <QueryClientProvider client={queryClient}>
                            <I18nProvider locale={APP_LOCALE}>
                                <Router />
                            </I18nProvider>
                        </QueryClientProvider>
                    </StoreProvider>
                </AppLoggerProvider>
            </LoadStageMarker>
        ),
        mode: isScreenHydrated || hasServerChrome ? 'hydrate' : 'render',
    })

    setTimeout(trackWebVitals)
}
